import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import PhotoDetailsWrapper from '../PhotoDetailsWrapper.tsx';
import FirmDetailsWrapper from '../FirmDetailsWrapper.tsx';
import MobilePhotoWrapper from 'dibs-interiors-photos/exports/MobilePhotoWrapper.tsx';
import PhotoDetailsSection from 'dibs-interiors-photos/exports/PhotoDetailsSection.tsx';
import PhotoDotsIndicator from 'dibs-interiors-photos/exports/PhotoDotsIndicator.tsx';
import MobilePhotoHeader from 'dibs-interiors-photos/exports/MobilePhotoHeader.tsx';
import { trackSavePhoto } from 'dibs-interiors-photos/exports/trackingHelpers.ts';
import DotsWrapper from '../../sharedComponents/interiors/DotsWrapper.tsx';
import PhotoSwiperWrapper from './PhotoSwiperWrapper.tsx';
import SocialIconsWrapper from '../../sharedComponents/interiors/SocialIconsWrapper.tsx';

import { type Photo } from 'dibs-interiors-photos/exports/types.ts';
import { type MobilePhotoContent_photoNode$key } from './__generated__/MobilePhotoContent_photoNode.graphql.ts';

type Props = {
    photoNode: MobilePhotoContent_photoNode$key | null | undefined;
    onPageChange: (url: string) => void;
    photos: Photo[];
    isModal: boolean;
    onCloseModal?: () => void;
};

const MobilePhotoContent: FC<Props> = props => {
    const { photoNode: photoNodeRef, photos, isModal, onCloseModal, onPageChange } = props;

    const photoNode = useFragment(
        graphql`
            fragment MobilePhotoContent_photoNode on TradePhotosQueryOutput {
                ...PhotoSwiperWrapper_photoNode
                ...PhotoDetailsWrapper_photoNode
                ...FirmDetailsWrapper_photoNode
                firmName
                projectName
                projectUrl
                photo {
                    id
                    path
                    url
                    serviceId
                    photoLike {
                        photoLikeId(userId: $userId) @include(if: $hasUserId)
                        totalPhotoLikes
                    }
                }
            }
        `,
        photoNodeRef
    );

    const photo = photoNode?.photo;
    const photoId = photo?.serviceId || '';
    const photoIndex = photos.findIndex(_photo => _photo?.serviceId === photo?.serviceId);
    const photoCount = photos.length;
    const projectName = photoNode?.projectName || '';
    const photoLike = photo?.photoLike;

    if (!photo?.path) {
        return null;
    }

    return (
        <>
            {isModal && <MobilePhotoHeader title={projectName} onCloseModal={onCloseModal} />}
            <MobilePhotoWrapper>
                <PhotoSwiperWrapper
                    photos={photos}
                    photoNode={photoNode}
                    onPageChange={onPageChange}
                    photoId={photoId}
                    photoIndex={photoIndex}
                />
                <SocialIconsWrapper
                    additionalSpacing={isModal}
                    photoNodeId={photo?.id}
                    photoId={photo?.serviceId || ''}
                    photoLikeId={photoLike?.photoLikeId || ''}
                    totalPhotoLikes={photoLike?.totalPhotoLikes || 0}
                    onPhotoLikeAdded={() => trackSavePhoto({ label: 'photo' })}
                    firmName={photoNode?.firmName || ''}
                    projectName={photoNode?.projectName || ''}
                    projectUrl={photoNode?.projectUrl || ''}
                    photoUrl={photo?.url || ''}
                    photoPath={photo?.path || ''}
                />
            </MobilePhotoWrapper>
            <DotsWrapper>
                <PhotoDotsIndicator currentIndex={photoIndex} totalItems={photoCount} />
            </DotsWrapper>
            <PhotoDetailsWrapper photoNode={photoNode} />
            <PhotoDetailsSection>
                <FirmDetailsWrapper photoNode={photoNode} />
            </PhotoDetailsSection>
        </>
    );
};

export default MobilePhotoContent;
